import type { ComponentProps } from 'react';

import type { MetricValues } from '@revelio/core';
import { BarChart, type BarData } from '@revelio/replots';

import { useCurrencyPreference } from '../../../../shared/user-preferences/hooks/useCurrencyPreference';
import type { SingleDataSet } from '../DataProvider/types';
import { RevelioPageId } from '../PageConfigProvider/types';
import type { PositionedItem } from '../PageConfigProvider/types/item';
import { deriveSharedChartType } from './chart-menu/plot-share/deriveSharedChartType';
import { usePlotShareData } from './chart-menu/plot-share/usePlotShare';
import { buildSentimentCountTooltip } from './utils/build-sentiment-count-tooltip';
import { getFormat } from './utils/getFormat';

type BarChartComponentProps = ComponentProps<typeof BarChart>;

export const Bar = ({
  data,
  pageId,
  item,
  metric,
}: {
  data: SingleDataSet;
  pageId?: string;
  item: PositionedItem;
  metric?: MetricValues;
}) => {
  const { code } = useCurrencyPreference();

  const isSentimentPage = [
    RevelioPageId.COMPANY_SENTIMENT,
    RevelioPageId.ROLE_SENTIMENT,
    RevelioPageId.GEOGRAPHY_SENTIMENT,
  ].includes(pageId as RevelioPageId);

  const barPoints = data.data;

  const tooltipContent = isSentimentPage
    ? buildSentimentCountTooltip({ data: barPoints, item, metric })
    : undefined;
  const barData: BarData[] = barPoints.map((point) => ({
    ...point,
    id: point.id ?? null,
    label: point.dim,
    ...(item?.plot?.color && { color: item.plot.color }),
  }));

  const maxValue = item.plot?.maxValue;
  const minValue = item.plot?.minValue;
  const chartProps: BarChartComponentProps = {
    data: barData,
    sort: item?.plot?.sort ?? null,
    format: item?.plot?.format ?? getFormat({ metric }),
    currencyCode: code,
    showBarLabels: item?.plot?.showDataLabels,
    ...((minValue !== undefined || maxValue !== undefined) && {
      xAxis: { min: minValue, max: maxValue },
    }),
    tooltipContent,
  };

  usePlotShareData({
    data: barData,
    chartType: deriveSharedChartType(BarChart),
    chartProps,
  });

  return <BarChart {...chartProps} />;
};
